import React, { useState, useCallback, useEffect } from "react";
import { X, UserPlus, Search } from "lucide-react";
import {
  copyMealOptionToClientPlan,
  fetchDietPlansForClient,
} from "../../../core/diet-plan/copyMealOptionToClientPlan";
import { fetchAllUserDetails } from "../../../../../service/SubscriptionWellDhanService";

/** Same event type as Users list (`/user` → UserListDashboard) */
const USER_LIST_EVENT_TYPE_ID = "4850387a-8838-45d5-8c65-dce9d1f86de6";

function userDisplayName(u) {
  if (!u) return "";
  const fn = u.first_name ?? u.user_first_name ?? "";
  const ln = u.last_name ?? u.user_last_name ?? "";
  const name = `${fn} ${ln}`.trim();
  return name || "Unknown user";
}

function userId(u) {
  if (!u) return null;
  return u.user_id ?? u.id ?? null;
}

function userEmail(u) {
  return (u?.email || u?.user_email || "").trim();
}

function userPhone(u) {
  return (u?.phone_number || u?.phone || "").trim();
}

function formatShortDate(s) {
  if (!s) return "—";
  try {
    const iso = String(s).includes("T") ? s : `${s}T12:00:00`;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return s;
  }
}

/** One line per plan: title + human-readable date range from durations */
function planLabel(p) {
  const title = p.title?.trim() || "Untitled plan";
  const durs = p.durations || [];
  if (!durs.length) return { title, range: "No date range" };
  const parts = durs.map((d) => {
    const a = formatShortDate(d.start_date);
    const b = formatShortDate(d.end_date);
    return `${a} – ${b}`;
  });
  return { title, range: parts.join(" · ") };
}

function pairKey(userId, planId) {
  return `${userId}::${planId}`;
}

/**
 * Copy the current meal option (all foods) to new options on other users' plans
 * (same meal type). Multi-select users and plans.
 */
const CopyMealOptionToClientPlanModal = ({
  isOpen,
  onClose,
  eventTypeId,
  periodName,
  optionTitle,
  sourceFoods,
  dietCommonUtils,
  onSuccess,
}) => {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [myUsersOnly, setMyUsersOnly] = useState(true);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  /** @type {Array<Record<string, unknown>>} */
  const [selectedUsers, setSelectedUsers] = useState([]);
  /** clientId -> { loading, plans, error } */
  const [plansByClient, setPlansByClient] = useState({});
  /** keys: `${clientId}::${planId}` */
  const [selectedPairs, setSelectedPairs] = useState({});

  const [copying, setCopying] = useState(false);
  const [error, setError] = useState(null);

  const reset = useCallback(() => {
    setSearchInput("");
    setDebouncedSearch("");
    setSearchResults([]);
    setSearchLoading(false);
    setSelectedUsers([]);
    setPlansByClient({});
    setSelectedPairs({});
    setError(null);
    setMyUsersOnly(true);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 500);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  useEffect(() => {
    if (!isOpen) return;
    if (debouncedSearch.length < 2) {
      setSearchResults([]);
      return;
    }

    let cancelled = false;
    (async () => {
      setSearchLoading(true);
      setError(null);
      try {
        const res = await fetchAllUserDetails(
          USER_LIST_EVENT_TYPE_ID,
          20,
          0,
          debouncedSearch,
          "user_joining_date",
          "desc",
          null,
          myUsersOnly
        );
        if (cancelled) return;
        if (res.success && Array.isArray(res.data?.users)) {
          setSearchResults(res.data.users);
        } else {
          setSearchResults([]);
        }
      } catch {
        if (!cancelled) setSearchResults([]);
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, isOpen, myUsersOnly]);

  const isUserSelected = (u) => {
    const id = userId(u);
    if (!id) return false;
    return selectedUsers.some((x) => userId(x) === id);
  };

  const addUser = async (u) => {
    const cid = userId(u);
    if (!cid || isUserSelected(u)) return;

    setSelectedUsers((prev) => [...prev, u]);
    setError(null);

    if (!eventTypeId) {
      setError("Event type is not configured.");
      return;
    }

    setPlansByClient((prev) => ({
      ...prev,
      [cid]: { loading: true, plans: [], error: null },
    }));

    try {
      const { ok, plans: list, message } = await fetchDietPlansForClient(
        eventTypeId,
        cid
      );
      if (!ok) {
        setPlansByClient((prev) => ({
          ...prev,
          [cid]: { loading: false, plans: [], error: message || "Load failed" },
        }));
        return;
      }
      if (!list?.length) {
        setPlansByClient((prev) => ({
          ...prev,
          [cid]: {
            loading: false,
            plans: [],
            error: "No active plans for this user.",
          },
        }));
        return;
      }
      setPlansByClient((prev) => ({
        ...prev,
        [cid]: { loading: false, plans: list, error: null },
      }));
    } catch {
      setPlansByClient((prev) => ({
        ...prev,
        [cid]: { loading: false, plans: [], error: "Failed to load plans." },
      }));
    }
  };

  const removeUser = (u) => {
    const cid = userId(u);
    if (!cid) return;
    setSelectedUsers((prev) => prev.filter((x) => userId(x) !== cid));
    setPlansByClient((prev) => {
      const next = { ...prev };
      delete next[cid];
      return next;
    });
    setSelectedPairs((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        if (k.startsWith(`${cid}::`)) delete next[k];
      });
      return next;
    });
  };

  const togglePlan = (clientId, planId) => {
    const k = pairKey(clientId, planId);
    setSelectedPairs((prev) => {
      const next = { ...prev };
      if (next[k]) delete next[k];
      else next[k] = true;
      return next;
    });
  };

  const selectAllPlansForUser = (clientId, plans) => {
    setSelectedPairs((prev) => {
      const next = { ...prev };
      (plans || []).forEach((p) => {
        if (p?.id) next[pairKey(clientId, p.id)] = true;
      });
      return next;
    });
  };

  const clearPlansForUser = (clientId) => {
    setSelectedPairs((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        if (k.startsWith(`${clientId}::`)) delete next[k];
      });
      return next;
    });
  };

  const handleCopy = async () => {
    setError(null);
    const pairs = Object.keys(selectedPairs).filter((k) => selectedPairs[k]);
    if (!pairs.length) {
      setError("Select at least one plan (under a user).");
      return;
    }
    if (!eventTypeId || !periodName) {
      setError("Missing configuration.");
      return;
    }
    if (!sourceFoods?.length) {
      setError("No foods to copy.");
      return;
    }

    setCopying(true);
    let okCount = 0;
    const errors = [];

    try {
      for (let i = 0; i < pairs.length; i++) {
        const [targetClientId, targetPlanId] = pairs[i].split("::");
        const result = await copyMealOptionToClientPlan({
          eventTypeId,
          targetClientId,
          targetPlanId,
          periodName,
          sourceFoods,
          dietCommonUtils,
        });
        if (result.ok) okCount += 1;
        else errors.push(result.message || "Copy failed");
      }

      if (okCount > 0) {
        const msg =
          errors.length > 0
            ? `Copied to ${okCount} plan(s). ${errors.length} failed: ${errors[0]}`
            : `Copied to ${okCount} plan(s).`;
        onSuccess?.(msg);
        onClose?.();
        reset();
      } else {
        setError(errors[0] || "Copy failed.");
      }
    } catch {
      setError("Copy failed. Please try again.");
    } finally {
      setCopying(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const selectedCount = Object.keys(selectedPairs).filter(
    (k) => selectedPairs[k]
  ).length;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="copy-option-modal-title"
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto overflow-x-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-orange-100 rounded-full">
              <UserPlus className="w-5 h-5 text-orange-600" />
            </div>
            <h2
              id="copy-option-modal-title"
              className="text-lg font-bold text-gray-900"
            >
              Copy option to other users&apos; plans
            </h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 p-1"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Copy <strong>{optionTitle || "this option"}</strong> ({periodName})
          into a new option on each selected plan. Pick users, then tick one or
          more plans per user.
        </p>

        <div className="space-y-4">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={myUsersOnly}
              onChange={(e) => setMyUsersOnly(e.target.checked)}
              className="rounded border-gray-300"
            />
            <span>Only my assigned users</span>
          </label>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Find users
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Type name (2+ characters)…"
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            {searchLoading && (
              <p className="text-xs text-gray-500 mt-1">Searching…</p>
            )}
            {debouncedSearch.length >= 2 && searchResults.length > 0 && (
              <ul className="mt-2 border border-gray-200 rounded-lg divide-y divide-gray-100">
                {searchResults.map((u, idx) => {
                  const id = userId(u);
                  const added = isUserSelected(u);
                  const em = userEmail(u);
                  const ph = userPhone(u);
                  return (
                    <li
                      key={id ? String(id) : `row-${idx}`}
                      className="flex items-stretch gap-2"
                    >
                      <div className="flex-1 min-w-0 px-3 py-2.5 text-sm">
                        <span className="font-medium text-gray-900 block">
                          {userDisplayName(u)}
                        </span>
                        <span className="block text-xs text-gray-600 mt-0.5">
                          {em || "—"}
                        </span>
                        <span className="block text-xs text-gray-600">
                          {ph || "—"}
                        </span>
                      </div>
                      <button
                        type="button"
                        disabled={added || !id}
                        onClick={() => addUser(u)}
                        className="shrink-0 px-3 text-sm font-medium text-orange-primary hover:bg-orange-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {added ? "Added" : "Add"}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            {debouncedSearch.length >= 2 &&
              !searchLoading &&
              searchResults.length === 0 && (
                <p className="text-xs text-gray-500 mt-1">No users found.</p>
              )}
          </div>

          {selectedUsers.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-800">
                Selected users & plans
              </p>
              {selectedUsers.map((u, userIdx) => {
                const cid = userId(u);
                const bucket = cid ? plansByClient[cid] : null;
                const loading = bucket?.loading;
                const plans = bucket?.plans || [];
                const loadErr = bucket?.error;

                return (
                  <div
                    key={cid ? String(cid) : `sel-${userIdx}`}
                    className="border border-gray-200 rounded-lg p-3 bg-gray-50/80"
                  >
                    <div className="flex justify-between gap-2 mb-2">
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 text-sm">
                          {userDisplayName(u)}
                        </div>
                        <div className="text-xs text-gray-600">{userEmail(u) || "—"}</div>
                        <div className="text-xs text-gray-600">{userPhone(u) || "—"}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeUser(u)}
                        className="text-sm text-red-600 hover:underline shrink-0"
                      >
                        Remove
                      </button>
                    </div>

                    {loading && (
                      <p className="text-xs text-gray-500">Loading plans…</p>
                    )}
                    {loadErr && !loading && (
                      <p className="text-xs text-amber-700">{loadErr}</p>
                    )}
                    {!loading && !loadErr && plans.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <div className="flex gap-2 mb-1">
                          <button
                            type="button"
                            className="text-xs text-orange-primary hover:underline"
                            onClick={() => selectAllPlansForUser(cid, plans)}
                          >
                            Select all
                          </button>
                          <button
                            type="button"
                            className="text-xs text-gray-500 hover:underline"
                            onClick={() => clearPlansForUser(cid)}
                          >
                            Clear
                          </button>
                        </div>
                        {plans.map((p) => {
                          const { title, range } = planLabel(p);
                          const k = pairKey(cid, p.id);
                          const checked = !!selectedPairs[k];
                          return (
                            <label
                              key={p.id}
                              className="flex items-start gap-2 cursor-pointer rounded px-1 py-1 hover:bg-white"
                            >
                              <input
                                type="checkbox"
                                className="mt-1 rounded border-gray-300"
                                checked={checked}
                                onChange={() => togglePlan(cid, p.id)}
                              />
                              <span className="text-sm min-w-0">
                                <span className="font-medium text-gray-900 block">
                                  {title}
                                </span>
                                <span className="text-xs text-gray-600">
                                  {range}
                                </span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {error && (
            <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 py-2 px-4 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCopy}
            disabled={copying || selectedCount === 0 || !sourceFoods?.length}
            className="flex-1 py-2 px-4 bg-orange-primary hover:bg-orange-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {copying
              ? "Copying…"
              : selectedCount
                ? `Copy to ${selectedCount} plan(s)`
                : "Copy"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CopyMealOptionToClientPlanModal;
